<script setup>
import {computed} from "vue";
import {useRouter} from "vue-router";
import {useBookingStore} from "@/stores/booking";
import StayCard from "@/components/StayCard.vue";
import AppIcon from "@/components/AppIcon.vue";
import ExtraArt from "@/components/ExtraArt.vue";
import {extras} from "@/misc/extras";

const bookingStore = useBookingStore()
const router = useRouter()

const isCheckedIn = computed(() => bookingStore.isCheckedIn)
</script>

<template>
  <div class="ww-screen ww-screen--tight">
    <stay-card/>

    <section v-if="!isCheckedIn" class="ww-card ww-card--accent checkin-cta">
      <div class="checkin-cta__head">
        <span class="ww-dot"></span>
        <h2 class="checkin-cta__title">{{ $t('home.checkinOpen') }}</h2>
      </div>
      <p class="checkin-cta__text">{{ $t('home.checkinPitch') }}</p>
      <router-link :to="{name: 'booking.checkin'}" class="ww-btn ww-btn--primary checkin-cta__btn">
        {{ $t('home.startCheckin') }}
        <app-icon name="arrow_forward" :size="20"/>
      </router-link>
    </section>

    <section v-else class="ww-card ww-card--success checked-in">
      <span class="checked-in__mark">
        <app-icon name="check" :size="21"/>
      </span>
      <div>
        <div class="checked-in__title">{{ $t('home.checkedInTitle') }}</div>
        <div class="checked-in__sub">{{ $t('home.checkedInSub') }}</div>
      </div>
    </section>

    <router-link :to="{name: 'booking.doorkeys'}" class="ww-row keys-row">
      <app-icon name="key" :size="26" class="keys-row__icon"/>
      <span class="ww-row__body">
        <span class="ww-row__title">{{ $t('doorKeys') }}</span>
        <span class="ww-row__sub">{{ isCheckedIn ? $t('home.keysUnlockedSub') : $t('home.keysBlockedSub') }}</span>
      </span>
      <app-icon name="chevron_right" :size="21" class="ww-row__chevron"/>
    </router-link>

    <template v-if="extras.length">
      <h2 class="ww-eyebrow extras__label">{{ $t('home.extrasLabel') }}</h2>
      <div class="extras ww-scroll">
        <button
            v-for="extra in extras"
            :key="extra.key"
            type="button"
            class="extras__card"
            @click="router.push({name: 'booking.contact'})"
        >
          <span class="extras__art">
            <extra-art :name="extra.key" :id="'art-' + extra.key"/>
          </span>
          <span class="extras__icon">
            <app-icon :name="extra.icon" :size="26"/>
          </span>
          <span class="extras__title">{{ $t('extras.' + extra.key + '.title') }}</span>
          <span class="extras__body">{{ $t('extras.' + extra.key + '.body') }}</span>
          <span class="extras__footer">
            <span class="extras__price">
              <span class="extras__price-from">{{ $t('home.extrasFrom') }}</span>
              {{ $t('extras.' + extra.key + '.price') }}
            </span>
            <span class="extras__cta">
              {{ $t('home.extrasCta') }}
              <app-icon name="arrow_forward" :size="16"/>
            </span>
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.checkin-cta {
  margin-top: 14px;
}

.checkin-cta__head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkin-cta__title {
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ww-text);
}

.checkin-cta__text {
  margin-top: 10px;
  font-size: 15px;
  line-height: 1.55;
  color: var(--ww-text-body);
}

.checkin-cta__btn {
  margin-top: 18px;
  border-radius: 13px;
  padding: 16px;
}

.checked-in {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
}

.checked-in__mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--ww-success);
  color: #fff;
  flex: 0 0 auto;
}

.checked-in__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--ww-success-ink);
}

.checked-in__sub {
  margin-top: 3px;
  font-size: 14px;
  color: var(--ww-success-muted);
}

.keys-row {
  margin-top: 14px;
  padding: 18px 20px;
}

.keys-row__icon {
  color: var(--ww-primary);
}

.keys-row .ww-row__title,
.keys-row .ww-row__sub {
  display: block;
}

.extras__label {
  margin-top: 26px;
}

.extras {
  display: flex;
  gap: 11px;
  margin: 12px calc(var(--ww-gutter) * -1) 0;
  padding: 2px var(--ww-gutter) 4px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  /* Without this the snap target aligns to the scrollport edge and eats the gutter. */
  scroll-padding-left: var(--ww-gutter);
}

.extras__card {
  position: relative;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  min-width: 268px;
  max-width: 320px;
  min-height: 300px;
  scroll-snap-align: start;
  text-align: left;
  /* Der Verlauf trägt die Karte — sie steht bewusst gegen die sonst helle,
     zurückhaltende Startseite und ist das einzige Werbeelement darauf. */
  background: linear-gradient(118deg, #6a4a9e 0%, #8e4d9f 42%, #c4588f 100%);
  border: none;
  border-radius: var(--ww-r-2xl);
  padding: 22px;
  overflow: hidden;
  isolation: isolate;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* A single extra has nothing to scroll next to, so it fills the row instead of
   leaving a gap where the next card would be. 100%, not none: the card does not
   shrink, so an uncapped one would grow to its max-content width and overflow. */
.extras__card:only-child {
  max-width: 100%;
}

.extras__card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(106, 74, 158, 0.32);
}

/* Die Szene sitzt rechts und wird vom Inhalt überlagert; sie darf am Rand
   auslaufen, damit die Karte nicht wie ein Bilderrahmen wirkt. */
/* Breite begrenzt statt über die Höhe skaliert: sonst wächst die Szene mit der
   Karte nach links und der Titel läuft in die Tür. */
.extras__art {
  position: absolute;
  top: 0;
  right: -10px;
  bottom: 0;
  z-index: -1;
  display: block;
  width: 56%;
  pointer-events: none;
}

.extras__icon {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: var(--ww-r-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.extras__title {
  margin-top: 30px;
  font-size: 27px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
}

.extras__body {
  flex: 1 1 auto;
  margin-top: 9px;
  max-width: 21ch;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.82);
}

.extras__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 22px;
}

.extras__price {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 27px;
  font-weight: 800;
  color: #fff;
}

.extras__price-from {
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
}

.extras__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 20px;
  border-radius: var(--ww-r-pill);
  background: #fff;
  font-size: 15px;
  font-weight: 700;
  color: #6a4a9e;
  white-space: nowrap;
}
</style>
